import { Modifier } from '../modifier';
import {
  ModifierPriority,
  ModifierTags,
  StatusEffect,
  type ModifierParams,
} from '../modifier.type';
import type { Player } from '../player.type';
import { actionHistory } from '../../../store';
import { openAmmo } from '../../modals/modals';
import { StatusEffectRegistry } from '../status-effects';
import { AmmoType } from '../ammo.type';

export const definitions = {
  [AmmoType.Poison]: {
    text: 'Deal 1 Damage, Apply 1 Poison.',
  },
  [AmmoType.Blood]: {
    text: 'Deal 1 Damage, Apply 1 Bleed.',
  },
  [AmmoType.Blind]: {
    text: 'Deal 1 Damage, Apply 1 Blind.',
  },
  [AmmoType.Web]: {
    text: 'Deal 1 Damage, Apply 1 Snare.',
  },
  [AmmoType.Burning]: {
    text: 'Deal 1 Damage, Apply 1 Burning.',
  },
  [AmmoType.Fist]: {
    text: 'Deal 3 Damage.',
  },
  [AmmoType.Silver]: {
    text: 'Deal 4 Damage.',
  },
  [AmmoType.Blank]: {
    text: 'Deal 2 Damage.',
  },
};

export class Ammo extends Modifier {
  static readonly baseTitle = 'Ammo';
  static readonly baseText = 'Stored ammo will be used when attacking with projectile weapons.';
  static readonly baseIcon = 'ammo';
  static readonly baseTags = [ModifierTags.Boon, ModifierTags.Ammo];
  static readonly basePriority = ModifierPriority.Middle;

  // Instance properties that can change
  title = Ammo.baseTitle;
  text = Ammo.baseText;
  icon = Ammo.baseIcon;
  tags: Array<ModifierTags> = [...Ammo.baseTags];
  priority = Ammo.basePriority;

  ammo: Array<AmmoType> = [];

  clone(newOwner: Player) {
    var clone = super.clone(newOwner) as Ammo;
    clone.ammo = [...this.ammo];
    clone.bonusIcon = this.bonusIcon;

    return clone;
  }

  init(params?: ModifierParams): void {
    if (!params || !params.ammoType) {
      throw new Error('Ammo modifier requires ammoType parameter on init.');
    }
    actionHistory.push(`Created ${this.stacks} ${this.type} on ${this.owner.title}`);

    this.ammo = [];
    for (let i = 0; i < this.stacks; i++) {
      this.ammo.push(params.ammoType);
    }
    this.bonusIcon = this.ammo[0];

    // restricting to 10 ammo
    while (this.ammo.length > 10) {
      this.ammo.shift();
    }

    this.stacks = this.ammo.length;
  }

  update(stacks: number, params: any) {
    for (let i = 0; i < stacks; i++) {
      this.ammo.push(params.ammoType);
    }
    // restricting to 10 ammo
    while (this.ammo.length > 10) {
      this.ammo.shift();
    }
    this.bonusIcon = this.ammo[0];
    this.stacks = this.ammo.length;

    if (this.owner) {
      actionHistory.push(`Added ${stacks} ${params.ammoType} ammo to ${this.owner.title}.`);
    }
  }

  getAmmo(): AmmoType {
    const ammo = this.ammo.shift();
    this.stacks = this.ammo.length;
    this.bonusIcon = this.ammo[0];

    if (this.stacks <= 0) {
      this.remove();
    }
    if (!ammo) {
      throw new Error('No ammo to get.');
    }
    return ammo;
  }

  onClick() {
    openAmmo({ ammo: this.ammo });
  }

  getHTML(): string {
    let definition = definitions[this.ammo[0]];
    return `
      <h3> ${this.ammo[0]} </h3>
      <p> ${definition.text} </p>
    `;
  }
}

// Register the status effect
StatusEffectRegistry.register(StatusEffect.Ammo, Ammo);
